<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance Detalhada dos Bots',
  },
});

const byBot = computed(() => props.data?.by_bot || []);

function getRateColor(rate) {
  if (rate >= 50) return 'text-n-teal-11';
  if (rate >= 30) return 'text-n-blue-11';
  if (rate >= 15) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-bot text-lg text-n-violet-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <span class="text-xs text-n-slate-11">
        {{ data.percentage_of_total || 0 }}% do total
      </span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-slate-12">
            {{ data.total_conversations || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Conversas</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-teal-11">
            {{ data.resolved || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Resolvidas</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div
            class="text-xl font-bold"
            :class="getRateColor(data.resolution_rate)"
          >
            {{ data.resolution_rate || 0 }}%
          </div>
          <div class="text-xs text-n-slate-11">Taxa Resolucao</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-amber-11">
            {{ data.handoffs_to_human || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Handoffs</div>
        </div>
      </div>

      <!-- Visual Breakdown -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Resultado das Conversas</span>
        <div class="flex h-6 rounded-lg overflow-hidden bg-n-alpha-1">
          <div
            v-if="data.resolved"
            class="bg-n-teal-9 flex items-center justify-center"
            :style="{ width: `${data.resolution_rate || 0}%` }"
          >
            <span v-if="data.resolution_rate >= 15" class="text-xs text-white">
              Resolvidas
            </span>
          </div>
          <div
            v-if="data.handoffs_to_human"
            class="bg-n-amber-9 flex items-center justify-center"
            :style="{ width: `${data.handoff_rate || 0}%` }"
          >
            <span v-if="data.handoff_rate >= 15" class="text-xs text-white">
              Handoffs
            </span>
          </div>
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>Resolvidas: {{ data.resolution_rate || 0 }}%</span>
          <span>Handoffs: {{ data.handoff_rate || 0 }}%</span>
        </div>
      </div>

      <!-- By Bot -->
      <div v-if="byBot.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Por Bot</span>
        <div class="flex flex-col gap-2">
          <div
            v-for="bot in byBot"
            :key="bot.bot_id"
            class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="i-lucide-bot text-n-violet-10" />
                <span class="text-sm font-medium text-n-slate-12">{{
                  bot.bot_name
                }}</span>
              </div>
              <span class="text-sm font-bold text-n-slate-12">{{
                bot.total_conversations
              }}</span>
            </div>
            <div
              class="flex items-center justify-between text-xs text-n-slate-11"
            >
              <span>{{ bot.resolved }} resolvidas</span>
              <span :class="getRateColor(bot.resolution_rate)">{{ bot.resolution_rate }}% taxa</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div class="grid grid-cols-2 gap-3 pt-2 border-t border-n-weak">
        <div class="text-center">
          <div class="text-lg font-bold text-n-slate-12">
            {{ data.total_messages || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Mensagens enviadas</div>
        </div>
        <div class="text-center">
          <div class="text-lg font-bold text-n-slate-12">
            {{ data.avg_messages_per_conversation || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Msgs/conversa</div>
        </div>
      </div>

      <!-- CSAT -->
      <div
        v-if="data.csat?.total_responses > 0"
        class="flex items-center justify-center text-xs text-n-slate-11"
      >
        <span>CSAT: {{ data.csat.average_rating }} ({{
            data.csat.total_responses
          }}
          avaliacoes)</span>
      </div>
    </div>
  </div>
</template>
